// Форматтеры для нового owner web.
//
// Концептуальные ограничения (roadmap «Концептуальные ограничения», handoff
// «Один primary-акцент на экран; моноширинно — числа, ID, PID, пути, время,
// версии, diff'ы (JetBrains Mono, tnum)»):
//   - числа моноширинно через CSS-стиль `MONO_NUMERIC_STYLE` (tnum для табличной
//     выравненности в колонках с числами)
//   - время — относительное «5м назад / 2ч назад / вчера»
//   - freshness — три порога handoff'а: fresh ≤ 5м, ok ≤ 1ч, stale > 1ч
//
// Не вводим `Intl.RelativeTimeFormat` без нужды: handoff жёстко русский, форматы
// короткие и предсказуемые. Если потребуется i18n — пересадим один точечный модуль.

import type { CSSProperties } from 'react'

/** CSS стиль для tabular-аligned моноширинных чисел. Накладывается на ячейки таблиц. */
export const MONO_NUMERIC_STYLE: CSSProperties = {
  fontFamily:
    "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: '"tnum" 1',
}

/**
 * Форматирует целое число с разрядными группами (`1 234 567`).
 * Тонкий неразрывный пробел как разделитель — Aurora type-style.
 */
export function formatInteger(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return Math.trunc(value).toLocaleString('ru-RU').replace(/ /g, ' ')
}

/**
 * Форматирует число с фиксированным числом знаков после запятой.
 */
export function formatDecimal(
  value: number | null | undefined,
  fractionDigits = 2,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return value
    .toLocaleString('ru-RU', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
    .replace(/ /g, ' ')
}

/**
 * Возраст значения относительно сейчас: "только что", "5м назад", "2ч назад",
 * "вчера", "3 дня назад", "5 мес назад", "год назад".
 *
 * Возвращает `'—'` для null/undefined/NaN. Будущие даты → "через 5м" etc.
 */
export function formatAge(
  value: Date | string | number | null | undefined,
  now: Date = new Date(),
): string {
  if (value === null || value === undefined) return '—'
  const date = value instanceof Date ? value : new Date(value)
  const ms = date.getTime()
  if (Number.isNaN(ms)) return '—'

  const diffMs = now.getTime() - ms
  const future = diffMs < 0
  const abs = Math.abs(diffMs)

  const sec = Math.floor(abs / 1000)
  if (sec < 10) return future ? 'через несколько секунд' : 'только что'
  if (sec < 60) return future ? `через ${sec}с` : `${sec}с назад`

  const min = Math.floor(sec / 60)
  if (min < 60) return future ? `через ${min}м` : `${min}м назад`

  const hr = Math.floor(min / 60)
  if (hr < 24) return future ? `через ${hr}ч` : `${hr}ч назад`

  const day = Math.floor(hr / 24)
  if (day === 1) return future ? 'завтра' : 'вчера'
  if (day < 30) return future ? `через ${day} дн` : `${day} дн назад`

  const month = Math.floor(day / 30)
  if (month < 12) return future ? `через ${month} мес` : `${month} мес назад`

  const year = Math.floor(month / 12)
  if (year === 1) return future ? 'через год' : 'год назад'
  return future ? `через ${year} г` : `${year} г назад`
}

/** Состояние свежести данных по handoff'у. */
export type FreshnessState = 'fresh' | 'ok' | 'stale' | 'unknown'

/**
 * Категоризирует возраст данных по handoff-порогам:
 *   - `fresh`: ≤ 5 минут
 *   - `ok`: ≤ 1 часа
 *   - `stale`: > 1 часа
 *   - `unknown`: null/невалидная дата
 */
export function getFreshness(
  value: Date | string | number | null | undefined,
  now: Date = new Date(),
): FreshnessState {
  if (value === null || value === undefined) return 'unknown'
  const date = value instanceof Date ? value : new Date(value)
  const ms = date.getTime()
  if (Number.isNaN(ms)) return 'unknown'
  const ageMs = Math.abs(now.getTime() - ms)
  const ageMin = ageMs / 60_000
  if (ageMin <= 5) return 'fresh'
  if (ageMin <= 60) return 'ok'
  return 'stale'
}

/**
 * Длительность в виде "1ч 23м" / "23м 4с" / "4с" (для uptime агентов и т.п.).
 * Не возвращает дни — uptime больше суток отдаём в формате `formatAge`.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—'
  const abs = Math.abs(ms)
  const sec = Math.floor(abs / 1000) % 60
  const min = Math.floor(abs / 60_000) % 60
  const hr = Math.floor(abs / 3_600_000)
  if (hr > 0) return `${hr}ч ${min}м`
  if (min > 0) return `${min}м ${sec}с`
  return `${sec}с`
}
